import anylogger from '@app/anylogger'
import { useLocalStorageOrState, useCurrentRef } from '@app/hooks'
import { alpha, Box, Button, IconButton, styled, useThemeProps } from '@mui/material'
import { useTreeViewApiRef } from '@mui/x-tree-view'
import {
	TreeViewPublicAPI,
	UseTreeViewExpansionSignature,
	UseTreeViewFocusSignature,
	UseTreeViewIconsSignature,
	UseTreeViewItemsSignature,
	UseTreeViewKeyboardNavigationSignature,
	UseTreeViewLabelSignature,
	UseTreeViewSelectionSignature
} from '@mui/x-tree-view/internals'
import { SimpleTreeView, SimpleTreeViewProps } from '@mui/x-tree-view/SimpleTreeView'
import { forwardRef, JSX, ReactElement, RefObject, useCallback, useEffect, useState } from 'react'
import { CustomTreeItem } from './TreeItem'
import { ITreeViewItem, ITreeViewProvider } from './TreeViewProvider'
import { TreeViewProviderAdapter } from './TreeViewProviderAdapter'
import Image from 'next/image'
// @ts-expect-error Can't seem to fix this error, even after adding svg files to the typescript config
import CollapseAll from './assets/collapseAll.svg'
// @ts-expect-error Can't seem to fix this error
import ExpandAll from './assets/expandAll.svg'
import { Flex, FlexC, FlexR } from './Flex'
import { Text } from './Text'
import React from 'react'
const log = anylogger('TreeView')

export type TreeViewApi = RefObject<
	| TreeViewPublicAPI<
			readonly [
				UseTreeViewItemsSignature,
				UseTreeViewExpansionSignature,
				UseTreeViewSelectionSignature,
				UseTreeViewFocusSignature,
				UseTreeViewKeyboardNavigationSignature,
				UseTreeViewIconsSignature,
				UseTreeViewLabelSignature
			]
	  >
	| undefined
>
interface TreeViewProps<T extends object = any, Multiple extends boolean = false> extends SimpleTreeViewProps<Multiple> {
	/**
	 * The data to be displayed that implements the ITreeNodeData interface */
	provider: ITreeViewProvider<T>

	/**
	 * If true, only expands or collapses the node if you click on the icon. */
	restrictExpansionToIcon?: boolean

	/**
	 * Default: undefined. Specifies the local storage key to store the expanded and selected nodes so that they are
	 * retained across browser refreshes.
	 */
	localStorageKey?: string

	/**
	 * If specified, these are the nodeIds of the selected items. Otherwise, the TreeView itself remembers
	 * what is selected, and if localStorageKey is specified, this is persisted across sessions
	 */
	selected?: Multiple extends true ? string[] : string

	/**
	 * This callback is fired when a tree node item is selected.
	 * data is the original data element supplied to the adapter.
	 * nodeData is the ITreeNodeData item (adapted item)
	 *  */
	onItemSelected?: (e: any, item: T) => void

	onlyShowEndIconOnHover?: boolean
	includeExpansionControls?: boolean
}
/**
 * This is a wrapper around TreeView that allows passing in a data object (or list of objects) that implement ITreeNodeData.
 * This allows you to create an adapter for any tree-like object and display it in the tree view. ITreeNodeData supports an async
 * getChildren method so that children are queried asynchronously.
 *
 * When a node's children are displayed, all those children
 * are queried to see if they have children, so there is a visual indication of those children.
 *
 * The styling is inherited entirely from MUI TreeView/TreeItem.
 */
export const TreeView = React.forwardRef(function TreeView<T extends object = any, Multiple extends boolean = false>(
	inProps: TreeViewProps<T, Multiple>,
	passedRef: React.Ref<HTMLElement>
) {
	type SelectType = Multiple extends true ? string[] : string
	const props = useThemeProps({ props: inProps, name: 'TreeView' })

	const {
		provider,
		sx,
		localStorageKey,
		selected: clientSelected,
		onItemSelected: onTreeNodeClick,
		restrictExpansionToIcon = false,
		onlyShowEndIconOnHover = false,
		includeExpansionControls = false,
		// fontSize,
		...rest
	} = props
	const { multiSelect } = rest

	// const [items, setItems] = useState<TreeNodeList>([])
	// const [iterator, setIterator] = useState(new TreeIterator<TreeNode>([]))

	// if (fontSize) sxProps = { ...sxProps, '& .MuiTreeItem-label': { fontSize: fontSize } }

	const [refreshCount, setRefreshCount] = useState(0)
	const refresh = useCallback(() => {
		setRefreshCount((prev) => prev + 1)
	}, [])

	const expandedKey = localStorageKey ? localStorageKey + '.expanded' : undefined
	const selectedKey = localStorageKey ? localStorageKey + '.selected' : undefined
	const [expanded, setExpanded] = useLocalStorageOrState<string[]>(expandedKey, [])
	const [selected, setSelected] = useLocalStorageOrState<SelectType>(selectedKey, (multiSelect ? [] : '') as SelectType)
	const [adapter, setAdapter] = useState<TreeViewProviderAdapter<T>>(new TreeViewProviderAdapter(provider))
	const [items, setItems] = useState<ITreeViewItem<T>[]>([])

	const [ref, setRef] = useCurrentRef<HTMLElement>(passedRef)

	const apiRef = useTreeViewApiRef()

	useEffect(() => {
		setAdapter(new TreeViewProviderAdapter(provider))
		provider.setApi(apiRef, refresh)
	}, [apiRef, provider, refresh])
	useEffect(() => {
		adapter.getItems(expanded).then((items) => {
			setItems(items)
			refresh()
		})
	}, [adapter, expanded, refresh])
	let sxProps: any = { ...sx, flexGrow: 1 }
	// if (fill) sxProps = { display: 'inlink-block', width: '100%', ...sxProps }

	const onExpandedChanged = useCallback(
		(e: any, nodeIds: string[]) => {
			setExpanded(nodeIds)
		},
		[setExpanded]
	)
	const onExpandedToggle = useCallback(async (e: any, itemId: string, isExpanded: boolean) => {
		// this is likely not necessary as we are passing the expanded (list) to adapter.getItems, and the useEffect is dependent on that.
	}, [])
	const onSelectionToggle = useCallback(
		async (e: any, itemId: string, isSelected: boolean) => {
			setSelected((prev) => {
				if (isSelected) {
					if (typeof prev === 'string') return itemId as SelectType
					if (multiSelect && !prev.includes(itemId)) {
						return [...(prev as string[]), itemId] as SelectType
					}
					return prev
				} else {
					if (typeof prev === 'string') return '' as SelectType
					if (prev.includes(itemId)) {
						return prev.filter((id) => id != itemId) as SelectType
					}
				}
				return prev as SelectType
			})
			if (isSelected) {
				const item = adapter.getItemById(items, itemId)
				if (item && onTreeNodeClick) {
					onTreeNodeClick(e, item.source)
				}
			}
		},
		[adapter, items, multiSelect, onTreeNodeClick, setSelected]
	)
	const expandAll = useCallback(
		(e: any, item?: ITreeViewItem) => {
			let expanded: string[] = []
			const addItems = (items: ITreeViewItem[]) => {
				for (let ii = 0; ii < items.length; ii++) {
					expanded.push(items[ii].id)
					addItems(items[ii].children ?? [])
				}
			}

			const children = item ? (item.children ?? []) : items
			addItems(children)
			setExpanded(expanded)
		},
		[items, setExpanded]
	)
	const collapseAll = useCallback(() => {
		setExpanded([])
	}, [setExpanded])
	const renderHeader = useCallback(() => {
		if (!includeExpansionControls) return null
		return (
			<FlexR alignItems="center">
				<Button
					onClick={expandAll}
					size="small"
					variant="text"
					startIcon={<Image width={16} height={16} src={CollapseAll} alt="Expand All" />}
					sx={{
						py: '0.25em',
						pr: '0.25em'
					}}
				>
					Expand All
				</Button>
				<Button
					onClick={collapseAll}
					size="small"
					variant="text"
					startIcon={<Image width={16} height={16} src={ExpandAll} alt="Collapse All" />}
					sx={{
						p: '0.25em'
					}}
				>
					Collapse All
				</Button>
			</FlexR>
		)
	}, [collapseAll, expandAll, includeExpansionControls])

	const renderChildren = useCallback(
		(item?: ITreeViewItem<T>) => {
			const children = item ? (item.children ?? []) : items

			return children.map((item: any) => {
				return (
					<CustomTreeItem
						key={item.id}
						itemId={item.id}
						label={item.label}
						endIcon={item?.endIcon}
						onlyShowEndIconOnHover={onlyShowEndIconOnHover}
						sxProps={item.sxProps}
					>
						{renderChildren(item)}
					</CustomTreeItem>
				)
			})
		},
		[items, onlyShowEndIconOnHover]
	)

	return (
		<FlexC fill>
			{renderHeader()}
			<Box ref={setRef} style={{ overflow: 'auto', width: '100%', height: '100%' }}>
				<SimpleTreeView
					apiRef={apiRef}
					sx={sxProps}
					onExpandedItemsChange={onExpandedChanged}
					onItemExpansionToggle={onExpandedToggle}
					expansionTrigger={restrictExpansionToIcon ? 'iconContainer' : 'content'}
					expandedItems={expanded}
					selectedItems={typeof clientSelected != 'undefined' ? clientSelected : selected}
					onItemSelectionToggle={onSelectionToggle}
				>
					{renderChildren()}
				</SimpleTreeView>
			</Box>
		</FlexC>
	)
}) as <T extends object = any, Multiple extends boolean = false>(
	props: TreeViewProps<T, Multiple> & { ref?: React.Ref<HTMLElement> }
) => JSX.Element

// NOTE: No point in doing this as this only allows you to override the root style as that is all we are specifying here.
// Any other slots, such as group, etc. are still called MuiTreeItem-group
// const DataTreeViewRoot = styled(TreeView, {
// 	name: 'DataTreeView',
// 	slot: 'Root',
// 	overridesResolver: (props: any, styles: any) => [styles.root],
// })(({ theme }) => ({}))
// const DataTreeItemRoot = styled(TreeItem, {
// 	name: 'DataTreeItem',
// 	slot: 'Root',
// 	overridesResolver: (props: any, styles: any) => [styles.root],
// })(({ theme }) => ({}))
